import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Crown, Sparkles, ArrowRight, Briefcase, Building2, TrendingUp, Hammer, Megaphone } from "lucide-react";

const RM = (n) => (n === 0 ? "RM0" : `RM${n.toFixed(2).replace(/\.00$/, "")}`);

const PLANS = [
  {
    id: "free",
    group: "Free",
    name: "Free Member",
    price: 0,
    cadence: "",
    audience: "Everyone",
    icon: Sparkles,
    desc: "Explore the network. No credit card required.",
    features: [
      "Browse all opportunities",
      "Apply to up to 3 / month",
      "In-app notifications",
      "Single territory request",
    ],
  },
  {
    id: "entrepreneur",
    group: "Individual",
    name: "Entrepreneur Member",
    price: 49.9,
    cadence: "/ month",
    audience: "Entrepreneurs",
    icon: Briefcase,
    desc: "Apply for agent, vendor, distributor, stockist, kiosk and territory roles.",
    features: [
      "Unlimited applications",
      "Featured profile in business owner searches",
      "Apply for territories (5 active)",
      "Priority application review",
    ],
  },
  {
    id: "affiliate_recruitment",
    group: "Business Owner",
    name: "Affiliate Recruitment Package",
    price: 99,
    cadence: "/ month",
    audience: "Business Owners",
    icon: Building2,
    desc: "Recruit affiliates and influencers at scale.",
    features: [
      "Up to 20 affiliate / influencer postings",
      "View applicant profiles",
      "Bulk approve / reject",
      "Branded company profile",
    ],
  },
  {
    id: "distributor_stockist",
    group: "Business Owner",
    name: "Distributor & Stockist Package",
    price: 199,
    cadence: "/ month",
    audience: "Business Owners",
    icon: Building2,
    desc: "Post distributor, stockist, vendor and agent recruitments.",
    featured: true,
    features: [
      "Unlimited distributor / stockist posts",
      "Advanced applicant filters",
      "Territory mapping",
      "Recruit influencers + affiliates",
    ],
  },
  {
    id: "territory_expansion",
    group: "Business Owner",
    name: "Territory Expansion Package",
    price: 399,
    cadence: "/ month",
    audience: "Business Owners",
    icon: Building2,
    desc: "Build a national distribution network through territory partnerships.",
    features: [
      "Everything in Distributor & Stockist",
      "Reserve territories before assignment",
      "Multi-state recruitment campaigns",
      "Custom application pipelines",
    ],
  },
  {
    id: "enterprise_growth",
    group: "Business Owner",
    name: "Enterprise Growth Package",
    price: 999,
    cadence: "/ month",
    audience: "Business Owners",
    icon: Crown,
    desc: "Full-stack recruitment, partnership and influencer ecosystem.",
    features: [
      "Everything in Territory Expansion",
      "Verified Enterprise badge",
      "Dedicated success manager",
      "Early API access",
      "Custom contracts & onboarding",
    ],
  },
  {
    id: "investor",
    group: "Investor",
    name: "Investor Membership",
    price: 199,
    cadence: "/ month",
    audience: "Investors",
    icon: TrendingUp,
    desc: "Discover deals and post investment opportunities.",
    features: [
      "Post investment opportunities",
      "Strategic partnership access",
      "Verified investor badge",
      "Direct messaging to founders (coming)",
    ],
  },
  {
    id: "contractor",
    group: "Contractor",
    name: "Contractor Membership",
    price: 29.9,
    cadence: "/ month",
    audience: "Contractors",
    icon: Hammer,
    desc: "Win contractor jobs across the network.",
    features: [
      "Unlimited job applications",
      "Featured contractor profile",
      "Direct alerts for new contracts",
      "Skill tags + portfolio",
    ],
  },
];

const GROUPS = ["Free", "Individual", "Business Owner", "Investor", "Contractor"];

export default function Membership() {
  const { user } = useAuth();
  const planId = user?.membership_plan || "free";

  return (
    <div className="space-y-10" data-testid="membership-page">
      <div>
        <div className="tag" data-testid="membership-tag">
          <span className="w-1.5 h-1.5 bg-[#C8102E]" /> Membership
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">
          Memberships built for Malaysia.
        </h1>
        <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
          Pick the membership that matches your path. All prices in Malaysian Ringgit (RM).
          Payments will activate in a later phase — until then, every member is on the <strong>Free</strong> tier and the platform is free to use.
        </p>
      </div>

      {GROUPS.map((group) => {
        const plans = PLANS.filter((p) => p.group === group);
        if (plans.length === 0) return null;
        return (
          <section key={group} data-testid={`membership-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="overline text-zinc-500">{group} packages</div>
              <div className="text-xs text-zinc-500 font-mono">{plans.length} plan{plans.length > 1 ? "s" : ""}</div>
            </div>
            <div className={`grid gap-4 ${plans.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-" + Math.min(plans.length, 4)}`}>
              {plans.map((p) => {
                const Icon = p.icon;
                const current = planId === p.id;
                return (
                  <div
                    key={p.id}
                    data-testid={`membership-plan-${p.id}`}
                    className={`relative border p-6 transition-colors ${
                      p.featured
                        ? "bg-[#09090b] text-white border-[#09090b]"
                        : "bg-white border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    {p.featured && (
                      <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-widest font-semibold bg-[#94959A] text-white px-2 py-0.5">
                        Most popular
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <Icon className={`w-6 h-6 ${p.featured ? "text-white" : "text-[#C8102E]"}`} />
                      <span className={`text-[10px] uppercase tracking-widest font-semibold ${p.featured ? "text-zinc-400" : "text-zinc-500"}`}>
                        {p.audience}
                      </span>
                    </div>
                    <h3 className="font-display font-black text-lg mt-4 tracking-tight">{p.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="font-display font-black text-3xl">{RM(p.price)}</span>
                      {p.cadence && <span className={`text-xs ${p.featured ? "text-zinc-400" : "text-zinc-500"}`}>{p.cadence}</span>}
                    </div>
                    <p className={`text-sm mt-3 ${p.featured ? "text-zinc-300" : "text-zinc-600"}`}>{p.desc}</p>
                    <ul className="mt-4 space-y-1.5 text-sm">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-white" : "text-[#C8102E]"}`} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid={`membership-choose-${p.id}`}
                      disabled={current}
                      onClick={() => alert("Payment integration is deferred. Architecture is ready when activated.")}
                      className={`mt-5 w-full inline-flex items-center justify-center gap-2 py-2.5 font-semibold text-sm transition-colors ${
                        current
                          ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                          : p.featured
                            ? "bg-white text-[#09090b] hover:bg-zinc-200"
                            : "border border-zinc-300 hover:bg-zinc-900 hover:text-white"
                      }`}
                    >
                      {current ? "Current plan" : `Choose ${p.name.split(" ")[0]}`}
                      {!current && <ArrowRight className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <div className="border border-zinc-200 bg-white p-6 flex items-start gap-3" data-testid="membership-note">
        <Sparkles className="w-4 h-4 mt-1 text-[#C8102E] shrink-0" />
        <div className="text-sm text-zinc-600">
          <strong className="text-zinc-900">Payments deferred.</strong> All RM prices are committed and the
          UI is ready. Stripe / Razorpay / Malaysian local processors (FPX, ToyyibPay) will plug into these
          plans when activated. Until then, every member is on the Free tier.
        </div>
      </div>
    </div>
  );
}
